import { getCreditPackageById } from '@/credits/packages';
import { ensureGoogleAdsBeginCheckoutConversionPending } from '@/analytics/google-ads-begin-checkout-server';
import { normalizeGoogleAdsEnhancedConversionEmail } from '@/analytics/google-ads-signup-policy';
import { websiteConfig } from '@/config/website';
import { getDb } from '@/db';
import {
  googleAdsBeginCheckoutConversion,
  googleAdsPurchaseConversion,
  payment,
} from '@/db/app.schema';
import { user } from '@/db/auth.schema';
import { serverEnv } from '@/env/server';
import {
  CHECKOUT_RETURN_OFFER_CAMPAIGN,
  CHECKOUT_RETURN_OFFER_DISCOUNT_PERCENT,
  CHECKOUT_RETURN_OFFER_PROMOTION_CODE,
  appendCheckoutReturnCancelled,
  isCheckoutReturnOfferPriceEligible,
  isOriginalSubscriptionCheckoutEligibleForReturnOffer,
} from '@/lib/checkout-return-offer';
import {
  createCheckoutReturnOfferToken,
  verifyCheckoutReturnOfferToken,
} from '@/lib/checkout-return-offer-token';
import { getLocale } from '@/lib/locale';
import {
  buildStripePaymentSuccessUrl,
  normalizeCheckoutReturn,
} from '@/lib/checkout-return';
import { getMonetizationTier } from '@/lib/monetization-tier';
import {
  findPlanByPriceId,
  findPriceInPlan,
  getAllPricePlans,
} from '@/lib/price-plan';
import { Routes } from '@/lib/routes';
import { getCanonicalUrl } from '@/lib/urls';
import { authApiMiddleware } from '@/middlewares/auth-middleware';
import {
  createCheckout,
  createCreditCheckout,
  createDownloadCheckout,
  createCustomerPortal,
  getStripeCheckoutSession,
} from '@/payment';
import type {
  PaymentScene,
  PaymentStatus,
  PlanInterval,
  PricePlan,
  Subscription,
} from '@/payment/types';
import { PaymentScenes, PaymentTypes } from '@/payment/types';
import {
  buildCreditCheckoutReturnUrls,
  normalizeLocalReturnPath,
} from '@/lib/credit-top-up';
import { createServerFn } from '@tanstack/react-start';
import { and, desc, eq, or } from 'drizzle-orm';
import { z } from 'zod';

type BeginCheckoutTrackingDetails = {
  conversionId: string | null;
  transactionId: string;
  enhancedConversionEmail: string;
  value: number;
  currency: string;
};

async function createBeginCheckoutTrackingDetails({
  currency,
  email,
  packageId,
  planId,
  priceId,
  scene,
  sessionId,
  userId,
  valueAmount,
}: {
  currency: string;
  email: string;
  packageId?: string;
  planId?: string;
  priceId: string;
  scene: PaymentScene;
  sessionId: string;
  userId: string;
  valueAmount: number;
}): Promise<BeginCheckoutTrackingDetails> {
  const provider = websiteConfig.payment?.provider;
  let conversionId: string | null = null;

  if (provider === 'stripe' || provider === 'creem') {
    try {
      conversionId = await ensureGoogleAdsBeginCheckoutConversionPending({
        currency,
        packageId,
        planId,
        priceId,
        provider,
        scene,
        sessionId,
        userId,
        valueAmount,
      });
    } catch (error) {
      console.error(
        'Queue Google Ads begin-checkout conversion failed:',
        error
      );
    }
  }

  return {
    conversionId,
    transactionId: sessionId,
    enhancedConversionEmail: normalizeGoogleAdsEnhancedConversionEmail(email),
    value: Math.max(0, Math.round(valueAmount)) / 100,
    currency: currency.trim().toUpperCase(),
  };
}

const checkoutSchema = z.object({
  planId: z.string().min(1),
  priceId: z.string().min(1),
  returnUrl: z.url().optional(),
  successUrl: z.url().optional(),
  cancelUrl: z.url().optional(),
  checkoutReturnOfferToken: z.string().min(1).max(4096).optional(),
  metadata: z.record(z.string(), z.string()).optional(),
});

export const createCheckoutSession = createServerFn({ method: 'POST' })
  .inputValidator(checkoutSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const db = getDb();
    const [userRow] = await db
      .select({ email: user.email, name: user.name })
      .from(user)
      .where(eq(user.id, userId))
      .limit(1);
    if (!userRow?.email) throw new Error('User email not found');
    const {
      planId,
      priceId,
      returnUrl,
      successUrl,
      cancelUrl,
      checkoutReturnOfferToken,
      metadata,
    } = data;
    const checkoutPrice = findPriceInPlan(planId, priceId);
    if (!checkoutPrice) throw new Error('Invalid checkout price');
    const checkoutPlan = findPlanByPriceId(priceId);
    if (!checkoutPlan || checkoutPlan.id !== planId) {
      throw new Error('Invalid checkout plan');
    }
    const locale = getLocale();
    const isCreem = websiteConfig.payment?.provider === 'creem';
    const isStripe = websiteConfig.payment?.provider === 'stripe';
    const checkoutInterval = checkoutPrice.interval;
    const priceIsOfferEligible = isCheckoutReturnOfferPriceEligible(
      checkoutPrice.type,
      checkoutInterval,
      checkoutPrice.currency
    );
    const hasPaid =
      isStripe && priceIsOfferEligible
        ? await hasPaidMonetization(userId)
        : false;
    const shouldIssueCheckoutReturnOffer =
      isStripe && priceIsOfferEligible && !hasPaid && !checkoutReturnOfferToken;

    let offerPayload:
      | Awaited<ReturnType<typeof verifyCheckoutReturnOfferToken>>
      | undefined;
    if (checkoutReturnOfferToken) {
      if (!isStripe || !priceIsOfferEligible || hasPaid) {
        throw new Error('Checkout-return offer is not available');
      }
      offerPayload = await verifyCheckoutReturnOfferToken({
        token: checkoutReturnOfferToken,
        userId,
        secret: serverEnv.BETTER_AUTH_SECRET,
      });
      if (
        offerPayload.planId !== planId ||
        offerPayload.priceId !== priceId ||
        offerPayload.interval !== checkoutInterval ||
        offerPayload.originalAmount !== checkoutPrice.amount ||
        offerPayload.currency !== checkoutPrice.currency.toUpperCase()
      ) {
        throw new Error('Checkout-return offer does not match this plan');
      }

      const originalSession = await getStripeCheckoutSession(
        offerPayload.originalSessionId
      );
      if (
        !isOriginalSubscriptionCheckoutEligibleForReturnOffer({
          status: originalSession.status,
          paymentStatus: originalSession.payment_status,
          mode: originalSession.mode,
          metadata: originalSession.metadata ?? {},
          userId,
          planId,
          priceId,
        })
      ) {
        throw new Error('Original checkout is not eligible for this offer');
      }
    }

    const baseUrl = getCanonicalUrl('/');
    const checkoutReturn = normalizeCheckoutReturn(
      returnUrl,
      baseUrl,
      Routes.SettingsBilling
    );
    const baseCancel = cancelUrl
      ? normalizeCheckoutReturn(cancelUrl, baseUrl, checkoutReturn.internal)
          .absolute
      : checkoutReturn.absolute;
    const cancel = shouldIssueCheckoutReturnOffer
      ? appendCheckoutReturnCancelled(baseCancel)
      : baseCancel;

    // For Stripe: {CHECKOUT_SESSION_ID} is replaced by Stripe on redirect,
    // then the Payment page polls by sessionId until the webhook writes the DB record.
    // For Creem: Creem does NOT replace URL placeholders and has its own
    // payment confirmation page, so redirect straight to billing.
    const success = isCreem
      ? successUrl
        ? normalizeCheckoutReturn(successUrl, baseUrl, checkoutReturn.internal)
            .absolute
        : checkoutReturn.absolute
      : successUrl
        ? normalizeCheckoutReturn(successUrl, baseUrl, checkoutReturn.internal)
            .absolute
        : buildStripePaymentSuccessUrl(baseUrl, checkoutReturn.internal);
    const checkoutMetadata = {
      ...metadata,
      userId,
      userName: userRow.name ?? '',
      ...(shouldIssueCheckoutReturnOffer
        ? { checkoutReturnOfferEligible: 'true' }
        : {}),
      ...(offerPayload
        ? {
            checkoutReturnOfferCampaign: CHECKOUT_RETURN_OFFER_CAMPAIGN,
            checkoutReturnOfferDiscountPercent:
              CHECKOUT_RETURN_OFFER_DISCOUNT_PERCENT.toString(),
            checkoutReturnOfferOriginalSessionId:
              offerPayload.originalSessionId,
            promotionCode: CHECKOUT_RETURN_OFFER_PROMOTION_CODE,
          }
        : {}),
    };

    const result = await createCheckout({
      planId,
      priceId,
      customerEmail: userRow.email,
      successUrl: success,
      cancelUrl: cancel,
      metadata: checkoutMetadata,
      locale,
      discount: offerPayload
        ? {
            stripePromotionCode: CHECKOUT_RETURN_OFFER_PROMOTION_CODE,
            percentOff: CHECKOUT_RETURN_OFFER_DISCOUNT_PERCENT,
            duration: 'once',
            firstTimeTransaction: true,
          }
        : undefined,
    });
    const beginCheckout = await createBeginCheckoutTrackingDetails({
      currency: checkoutPrice.currency,
      email: userRow.email,
      planId,
      priceId,
      scene:
        checkoutPrice.type === PaymentTypes.SUBSCRIPTION
          ? PaymentScenes.SUBSCRIPTION
          : PaymentScenes.LIFETIME,
      sessionId: result.id,
      userId,
      valueAmount: checkoutPrice.amount,
    });
    const checkoutReturnOffer =
      shouldIssueCheckoutReturnOffer && checkoutInterval
        ? await createCheckoutReturnOfferToken({
            userId,
            originalSessionId: result.id,
            planId,
            priceId,
            interval: checkoutInterval,
            originalAmount: checkoutPrice.amount,
            currency: checkoutPrice.currency,
            secret: serverEnv.BETTER_AUTH_SECRET,
          }).then(({ token, expiresAtMs }) => ({
            token,
            planId,
            planName: checkoutPlan.name ?? planId,
            priceId,
            interval: checkoutInterval,
            originalAmount: checkoutPrice.amount,
            currency: checkoutPrice.currency,
            expiresAtMs,
            returnTo: checkoutReturn.internal,
          }))
        : undefined;
    return {
      url: result.url,
      id: result.id,
      beginCheckout,
      checkoutReturnOffer,
    };
  });

const portalSchema = z.object({
  returnUrl: z.string().url().optional(),
  locale: z.string().optional(),
});

export const createCustomerPortalSession = createServerFn({ method: 'POST' })
  .inputValidator(portalSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const db = getDb();
    const [row] = await db
      .select({ customerId: user.customerId })
      .from(user)
      .where(eq(user.id, userId))
      .limit(1);
    if (!row?.customerId) {
      throw new Error('No customer found for user');
    }
    const locale = getLocale();
    const returnUrl = data.returnUrl ?? getCanonicalUrl(Routes.SettingsBilling);
    const result = await createCustomerPortal({
      customerId: row.customerId,
      returnUrl,
      locale: data.locale ?? locale,
    });
    return { url: result.url };
  });

export const getCurrentPlan = createServerFn({ method: 'GET' })
  .middleware([authApiMiddleware])
  .handler(async ({ context }) => {
    const { userId } = context;
    const db = getDb();
    const plans = getAllPricePlans();
    const freePlan = plans.find((p) => p.isFree && !p.disabled) ?? null;
    const lifetimePlanIds = plans.filter((p) => p.isLifetime).map((p) => p.id);

    const payments = await db
      .select({
        id: payment.id,
        priceId: payment.priceId,
        customerId: payment.customerId,
        type: payment.type,
        status: payment.status,
        scene: payment.scene,
        interval: payment.interval,
        periodStart: payment.periodStart,
        periodEnd: payment.periodEnd,
        cancelAtPeriodEnd: payment.cancelAtPeriodEnd,
        trialStart: payment.trialStart,
        trialEnd: payment.trialEnd,
        createdAt: payment.createdAt,
      })
      .from(payment)
      .where(
        and(
          eq(payment.paid, true),
          eq(payment.userId, userId),
          or(
            and(
              eq(payment.type, PaymentTypes.ONE_TIME),
              eq(payment.scene, PaymentScenes.LIFETIME),
              eq(payment.status, 'completed')
            ),
            and(
              eq(payment.type, PaymentTypes.ONE_TIME),
              eq(payment.scene, PaymentScenes.CREDIT),
              eq(payment.status, 'completed')
            ),
            and(
              eq(payment.type, PaymentTypes.SUBSCRIPTION),
              or(eq(payment.status, 'active'), eq(payment.status, 'trialing'))
            )
          )
        )
      )
      .orderBy(desc(payment.createdAt));

    let userLifetimePlan: PricePlan | null = null;
    let activeSubscription: Subscription | null = null;
    let hasPurchasedCredits = false;

    for (const rec of payments) {
      if (
        rec.type === PaymentTypes.ONE_TIME &&
        rec.scene === PaymentScenes.CREDIT &&
        rec.status === 'completed'
      ) {
        hasPurchasedCredits = true;
      }
      if (
        rec.type === PaymentTypes.ONE_TIME &&
        rec.scene === PaymentScenes.LIFETIME &&
        rec.status === 'completed' &&
        !userLifetimePlan
      ) {
        const plan = findPlanByPriceId(rec.priceId);
        if (plan && lifetimePlanIds.includes(plan.id)) {
          userLifetimePlan = plan as PricePlan;
        }
      }
      if (
        !userLifetimePlan &&
        rec.type === PaymentTypes.SUBSCRIPTION &&
        (rec.status === 'active' || rec.status === 'trialing') &&
        !activeSubscription
      ) {
        activeSubscription = {
          id: rec.id,
          priceId: rec.priceId,
          customerId: rec.customerId,
          status: rec.status as PaymentStatus,
          type: rec.type as 'subscription',
          interval: rec.interval as PlanInterval | undefined,
          currentPeriodStart: rec.periodStart ?? undefined,
          currentPeriodEnd: rec.periodEnd ?? undefined,
          cancelAtPeriodEnd: rec.cancelAtPeriodEnd ?? false,
          trialStartDate: rec.trialStart ?? undefined,
          trialEndDate: rec.trialEnd ?? undefined,
          createdAt: rec.createdAt,
        };
      }
    }

    const monetizationTier = getMonetizationTier({
      hasLifetimePlan: Boolean(userLifetimePlan),
      hasActiveSubscription: Boolean(activeSubscription),
      hasPurchasedCredits,
    });

    if (userLifetimePlan) {
      return {
        currentPlan: userLifetimePlan,
        subscription: null,
        monetizationTier,
      };
    }
    if (activeSubscription) {
      const subscriptionPlan =
        plans.find((p) =>
          p.prices.some((pr) => pr.priceId === activeSubscription!.priceId)
        ) ?? null;
      return {
        currentPlan: subscriptionPlan as PricePlan | null,
        subscription: activeSubscription,
        monetizationTier,
      };
    }
    return {
      currentPlan: freePlan as PricePlan | null,
      subscription: null,
      monetizationTier,
    };
  });

const checkCompletionSchema = z.object({ sessionId: z.string().min(1) });

/**
 * Check payment completion by Stripe session ID.
 * Used by Stripe flow where the session ID is embedded in the redirect URL.
 */
export const checkPaymentCompletion = createServerFn({ method: 'GET' })
  .inputValidator(checkCompletionSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const db = getDb();
    const [record] = await db
      .select()
      .from(payment)
      .where(
        and(
          eq(payment.sessionId, data.sessionId),
          eq(payment.userId, context.userId)
        )
      )
      .limit(1);
    const [checkout] = await db
      .select({ id: googleAdsBeginCheckoutConversion.id })
      .from(googleAdsBeginCheckoutConversion)
      .where(
        and(
          eq(googleAdsBeginCheckoutConversion.userId, context.userId),
          eq(googleAdsBeginCheckoutConversion.sessionId, data.sessionId)
        )
      )
      .limit(1);
    const [conversion] = await db
      .select({
        currency: googleAdsPurchaseConversion.currency,
        scene: googleAdsPurchaseConversion.scene,
        transactionId: googleAdsPurchaseConversion.transactionId,
        valueAmount: googleAdsPurchaseConversion.valueAmount,
      })
      .from(googleAdsPurchaseConversion)
      .where(
        and(
          eq(googleAdsPurchaseConversion.userId, context.userId),
          eq(googleAdsPurchaseConversion.sessionId, data.sessionId)
        )
      )
      .limit(1);
    return {
      isOwnedSession: Boolean(checkout || record || conversion),
      isPaid: !!record?.paid,
      purchase: conversion
        ? {
            currency: conversion.currency,
            scene: conversion.scene,
            transactionId: conversion.transactionId,
            value: Math.max(0, conversion.valueAmount) / 100,
          }
        : null,
    };
  });

const localReturnPathSchema = z.string().refine(normalizeLocalReturnPath, {
  message: 'Return path must be a local application path.',
});

const creditCheckoutSchema = z.object({
  packageId: z.string().min(1),
  returnUrl: z.url().optional(),
  successUrl: z.url().optional(),
  cancelUrl: z.url().optional(),
  returnTo: localReturnPathSchema.optional(),
  metadata: z.record(z.string(), z.string()).optional(),
});

async function hasPaidMonetization(userId: string) {
  const [paidRecord] = await getDb()
    .select({ id: payment.id })
    .from(payment)
    .where(and(eq(payment.paid, true), eq(payment.userId, userId)))
    .limit(1);
  return Boolean(paidRecord);
}

/** Create a credit-package checkout session. */
export const createCreditCheckoutSession = createServerFn({ method: 'POST' })
  .inputValidator(creditCheckoutSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const { userId } = context;

    if (!websiteConfig.credits?.enable) {
      throw new Error('Credits are disabled');
    }

    const db = getDb();
    const [userRow] = await db
      .select({ email: user.email, name: user.name })
      .from(user)
      .where(eq(user.id, userId))
      .limit(1);
    if (!userRow?.email) throw new Error('User email not found');

    const { packageId, returnUrl, successUrl, cancelUrl, returnTo, metadata } =
      data;

    const creditPackage = getCreditPackageById(packageId);
    if (!creditPackage) {
      throw new Error(`Invalid credit package: ${packageId}`);
    }
    const priceId = creditPackage.price.priceId;
    if (!priceId) {
      throw new Error(`No valid price found for credit package: ${packageId}`);
    }

    const baseUrl = getCanonicalUrl('/');
    const isCreem = websiteConfig.payment?.provider === 'creem';
    const checkoutReturn = normalizeCheckoutReturn(
      returnUrl,
      baseUrl,
      Routes.SettingsCredits
    );
    const contextualUrls = returnTo
      ? buildCreditCheckoutReturnUrls({ baseUrl, isCreem, returnTo })
      : undefined;
    const cancel = cancelUrl
      ? normalizeCheckoutReturn(cancelUrl, baseUrl, checkoutReturn.internal)
          .absolute
      : (contextualUrls?.cancelUrl ?? checkoutReturn.absolute);
    const success = isCreem
      ? successUrl
        ? normalizeCheckoutReturn(successUrl, baseUrl, checkoutReturn.internal)
            .absolute
        : (contextualUrls?.successUrl ?? checkoutReturn.absolute)
      : successUrl
        ? normalizeCheckoutReturn(successUrl, baseUrl, checkoutReturn.internal)
            .absolute
        : (contextualUrls?.successUrl ??
          buildStripePaymentSuccessUrl(baseUrl, checkoutReturn.internal));

    const checkoutMetadata = {
      ...metadata,
      userId,
      userName: userRow.name ?? '',
    };

    const result = await createCreditCheckout({
      packageId,
      priceId,
      customerEmail: userRow.email,
      successUrl: success,
      cancelUrl: cancel,
      metadata: checkoutMetadata,
    });
    const beginCheckout = await createBeginCheckoutTrackingDetails({
      currency: creditPackage.price.currency,
      email: userRow.email,
      packageId,
      priceId,
      scene: PaymentScenes.CREDIT,
      sessionId: result.id,
      userId,
      valueAmount: creditPackage.price.amount,
    });
    return {
      url: result.url,
      id: result.id,
      beginCheckout,
    };
  });

const SINGLE_DOWNLOAD_PRICE_ID = 'single-download-usd-99';
const SINGLE_DOWNLOAD_PRICE_CENTS = 99;

const downloadCheckoutSchema = z.object({
  resultId: z.string().min(1).max(2048),
  assetType: z.enum(['image', 'video']),
  returnUrl: z.url().optional(),
  returnTo: localReturnPathSchema.optional(),
  metadata: z.record(z.string(), z.string()).optional(),
});

/** Create a $0.99 checkout that unlocks only the selected result. */
export const createDownloadCheckoutSession = createServerFn({ method: 'POST' })
  .inputValidator(downloadCheckoutSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const db = getDb();
    const [userRow] = await db
      .select({ email: user.email, name: user.name })
      .from(user)
      .where(eq(user.id, userId))
      .limit(1);
    if (!userRow?.email) throw new Error('User email not found');

    const baseUrl = getCanonicalUrl('/');
    const checkoutReturn = normalizeCheckoutReturn(
      data.returnUrl,
      baseUrl,
      Routes.SettingsCredits
    );
    const contextualUrls = data.returnTo
      ? buildCreditCheckoutReturnUrls({
          baseUrl,
          isCreem: false,
          returnTo: data.returnTo,
        })
      : undefined;
    const cancelUrl = contextualUrls?.cancelUrl ?? checkoutReturn.absolute;
    const successUrl =
      contextualUrls?.successUrl ??
      buildStripePaymentSuccessUrl(baseUrl, checkoutReturn.internal);

    const result = await createDownloadCheckout({
      assetType: data.assetType,
      resultId: data.resultId,
      customerEmail: userRow.email,
      successUrl,
      cancelUrl,
      metadata: {
        ...data.metadata,
        userId,
        userName: userRow.name ?? '',
      },
    });
    const beginCheckout = await createBeginCheckoutTrackingDetails({
      currency: 'USD',
      email: userRow.email,
      priceId: SINGLE_DOWNLOAD_PRICE_ID,
      scene: PaymentScenes.DOWNLOAD,
      sessionId: result.id,
      userId,
      valueAmount: SINGLE_DOWNLOAD_PRICE_CENTS,
    });

    return { url: result.url, id: result.id, beginCheckout };
  });

const downloadEntitlementSchema = z.object({
  resultId: z.string().min(1).max(2048),
  assetType: z.enum(['image', 'video']),
});

/** Check whether the current user bought this exact result download. */
export const hasDownloadEntitlement = createServerFn({ method: 'GET' })
  .inputValidator(downloadEntitlementSchema)
  .middleware([authApiMiddleware])
  .handler(async ({ data, context }) => {
    const db = getDb();
    const records = await db
      .select({ checkoutMetadata: payment.checkoutMetadata })
      .from(payment)
      .where(
        and(
          eq(payment.userId, context.userId),
          eq(payment.scene, PaymentScenes.DOWNLOAD),
          eq(payment.paid, true),
          eq(payment.status, 'completed')
        )
      )
      .orderBy(desc(payment.createdAt));

    const entitled = records.some((record) => {
      if (!record.checkoutMetadata) return false;
      try {
        const metadata = JSON.parse(record.checkoutMetadata) as Record<
          string,
          unknown
        >;
        return (
          metadata.resultId === data.resultId &&
          metadata.assetType === data.assetType
        );
      } catch {
        return false;
      }
    });

    return { entitled };
  });
